import featherIcons from 'feather-icons/dist/icons.json'
import lucideNodes from 'lucide-static/icon-nodes.json'

type LucideNode = [tag: string, attrs: Record<string, string | number>]

const feather = featherIcons as Record<string, string>
const lucide = lucideNodes as unknown as Record<string, LucideNode[]>

/**
 * The icon set is Lucide (lucide-static, 1,900 outline icons on Feather's 24 px / 2 px-stroke grid), looked up by
 * Feather's naming so the legacy names keep working: `MonitorIcon` and `monitor` both resolve. Lucide renamed a
 * number of Feather icons (`alert-circle` → `circle-alert`, `edit` → `square-pen`, `home` → `house`, …): `ALIASES`
 * maps every Feather name Lucide no longer has. The few it dropped altogether — the brand marks (`facebook`,
 * `linkedin`, `github`, …) and the face icons — still come from the feather-icons package, which stays installed
 * for them only. Lookup order: Lucide by name, Lucide by alias, Feather.
 */
const ALIASES: Record<string, string> = {
  'alert-circle': 'circle-alert',
  'alert-octagon': 'octagon-alert',
  'alert-triangle': 'triangle-alert',
  'align-center': 'text-align-center',
  'align-justify': 'text-align-justify',
  'align-left': 'text-align-start',
  'align-right': 'text-align-end',
  'arrow-down-circle': 'circle-arrow-down',
  'arrow-left-circle': 'circle-arrow-left',
  'arrow-right-circle': 'circle-arrow-right',
  'arrow-up-circle': 'circle-arrow-up',
  'bar-chart': 'chart-column',
  'bar-chart-2': 'chart-column',
  'check-circle': 'circle-check',
  'check-square': 'square-check',
  columns: 'columns-2',
  'divide-circle': 'circle-divide',
  'divide-square': 'square-divide',
  'download-cloud': 'cloud-download',
  edit: 'square-pen',
  'edit-2': 'pen',
  'edit-3': 'pen-line',
  filter: 'funnel',
  frown: 'face-slightly-frowning',
  'git-commit': 'git-commit-horizontal',
  grid: 'grid-3x3',
  'help-circle': 'circle-question-mark',
  home: 'house',
  layout: 'panels-top-left',
  meh: 'face-neutral',
  'minus-circle': 'circle-minus',
  'minus-square': 'square-minus',
  'more-horizontal': 'ellipsis',
  'more-vertical': 'ellipsis-vertical',
  'pause-circle': 'circle-pause',
  'pie-chart': 'chart-pie',
  'play-circle': 'circle-play',
  'plus-circle': 'circle-plus',
  'plus-square': 'square-plus',
  sidebar: 'panel-left',
  sliders: 'sliders-horizontal',
  smile: 'face-slightly-smiling',
  'stop-circle': 'circle-stop',
  tool: 'wrench',
  'trash-2': 'trash',
  unlock: 'lock-open',
  'upload-cloud': 'cloud-upload',
  'x-circle': 'circle-x',
  'x-octagon': 'octagon-x',
  'x-square': 'square-x',
}

/**
 * Legacy icon names are Feather component names (`MonitorIcon`, `Trash2Icon`, `LogInIcon`, 186 files); the sets'
 * own names are kebab-case (`monitor`, `trash-2`, `log-in`). Both are accepted; this is the kebab-case form.
 */
export function featherName(name: string): string {
  return name
    .replace(/Icon$/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([a-zA-Z])(\d)/g, '$1-$2')
    .toLowerCase()
}

function escapeAttr(value: string | number): string {
  return String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
}

function renderLucide(nodes: LucideNode[]): string {
  return nodes
    .map(([tag, attrs]) => {
      const attributes = Object.entries(attrs)
        .map(([key, value]) => ` ${key}="${escapeAttr(value)}"`)
        .join('')
      return `<${tag}${attributes}></${tag}>`
    })
    .join('')
}

const rendered = new Map<string, string>()

/** The inner SVG markup of an icon (Lucide, else Feather for the names Lucide dropped), or `null` for an unknown name. */
export function featherContents(name: string): string | null {
  const key = featherName(name)
  const cached = rendered.get(key)
  if (cached !== undefined) return cached
  const nodes = lucide[key] ?? (ALIASES[key] ? lucide[ALIASES[key]] : undefined)
  const contents = nodes ? renderLucide(nodes) : (feather[key] ?? null)
  if (contents !== null) rendered.set(key, contents)
  return contents
}

export function hasIcon(name: string): boolean {
  return featherContents(name) !== null
}
